import { IconButton, InputAdornment, Popover, TextField } from "@mui/material";
import * as React from "react";

import type { TimeDisplayMode } from "@/shared/components/visualization/WorkspaceCtx";
import { CalendarTodayIcon } from "@/shared/foundation/icons/mui";

import { BetweenDateTimeSelector } from "./BetweenDateTimeSelector";
import styles from "./DatetimeFilterInput.module.css";
import { DateTimeSelector } from "./DateTimeSelector";
import { formatDatetimeValue } from "./formatDatetimeValue";

// ---------------------------------------------------------------------------
// Public props
// ---------------------------------------------------------------------------

interface TextFieldOverrides {
  fullWidth?: boolean;
  variant?: "outlined" | "standard" | "filled";
  className?: string;
  disabled?: boolean;
}

interface DatetimeFilterInputBaseProps {
  timeDisplayMode: TimeDisplayMode;
  earliestTime: bigint | null;
  /** Extra props forwarded to each TextField. */
  textFieldProps?: TextFieldOverrides;
  /** Pass true when nested inside another Popover (e.g. ColumnFilterPopover). */
  disablePopoverRestoreFocus?: boolean;
}

interface SingleProps extends DatetimeFilterInputBaseProps {
  mode: "single";
  value: string;
  onChange: (value: string) => void;
}

interface BetweenProps extends DatetimeFilterInputBaseProps {
  mode: "between";
  from: string;
  to: string;
  onChange: (from: string, to: string) => void;
  /**
   * "split" renders two separate From / To TextFields.
   * "combined" renders one TextField showing "From — To".
   * @default "split"
   */
  layout?: "split" | "combined";
}

export type DatetimeFilterInputProps = SingleProps | BetweenProps;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function DatetimeFilterInput(props: DatetimeFilterInputProps) {
  const [calendarAnchor, setCalendarAnchor] =
    React.useState<HTMLElement | null>(null);
  const calendarOpen = Boolean(calendarAnchor);
  const calendarIconRef = React.useRef<HTMLButtonElement>(null);
  const [betweenInitialTab, setBetweenInitialTab] = React.useState<
    "from" | "to"
  >("from");

  const closeCalendar = React.useCallback(() => setCalendarAnchor(null), []);

  const {
    timeDisplayMode,
    earliestTime,
    textFieldProps,
    disablePopoverRestoreFocus,
  } = props;

  const format = React.useCallback(
    (v: string) => formatDatetimeValue(v, timeDisplayMode, earliestTime),
    [timeDisplayMode, earliestTime],
  );

  // -----------------------------------------------------------------------
  // Single mode
  // -----------------------------------------------------------------------
  if (props.mode === "single") {
    return (
      <>
        <ReadOnlyDateField
          placeholder="Select date/time..."
          value={format(props.value)}
          ariaLabel="Filter value"
          onOpen={(anchor) => setCalendarAnchor(anchor)}
          calendarIconRef={calendarIconRef}
          textFieldProps={textFieldProps}
        />
        <Popover
          open={calendarOpen}
          anchorEl={calendarAnchor}
          onClose={closeCalendar}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          disableRestoreFocus={disablePopoverRestoreFocus}
        >
          <DateTimeSelector
            value={props.value}
            onChange={props.onChange}
            timeDisplayMode={timeDisplayMode}
            onConfirm={closeCalendar}
          />
        </Popover>
      </>
    );
  }

  // -----------------------------------------------------------------------
  // Between mode
  // -----------------------------------------------------------------------
  const layout = props.layout ?? "split";

  return (
    <>
      {layout === "split" ? (
        <>
          <ReadOnlyDateField
            placeholder="From..."
            value={format(props.from)}
            ariaLabel="Filter minimum value"
            onOpen={(anchor) => {
              setBetweenInitialTab("from");
              setCalendarAnchor(anchor);
            }}
            calendarIconRef={calendarIconRef}
            textFieldProps={textFieldProps}
          />
          <ReadOnlyDateField
            placeholder="To..."
            value={format(props.to)}
            ariaLabel="Filter maximum value"
            onOpen={(anchor) => {
              setBetweenInitialTab("to");
              setCalendarAnchor(anchor);
            }}
            textFieldProps={textFieldProps}
          />
        </>
      ) : (
        <ReadOnlyDateField
          placeholder="From — To"
          value={
            props.from || props.to
              ? `${format(props.from)} — ${format(props.to)}`
              : ""
          }
          ariaLabel="Filter value"
          onOpen={(anchor) => setCalendarAnchor(anchor)}
          calendarIconRef={calendarIconRef}
          textFieldProps={textFieldProps}
        />
      )}
      <Popover
        open={calendarOpen}
        anchorEl={calendarAnchor}
        onClose={closeCalendar}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        disableRestoreFocus={disablePopoverRestoreFocus}
      >
        <BetweenDateTimeSelector
          from={props.from}
          to={props.to}
          onChange={props.onChange}
          timeDisplayMode={timeDisplayMode}
          initialTab={betweenInitialTab}
          onConfirm={closeCalendar}
        />
      </Popover>
    </>
  );
}

// ---------------------------------------------------------------------------
// Private helper — the repeated TextField + calendar icon pattern
// ---------------------------------------------------------------------------

interface ReadOnlyDateFieldProps {
  placeholder: string;
  value: string;
  ariaLabel: string;
  onOpen: (anchor: HTMLElement) => void;
  calendarIconRef?: React.RefObject<HTMLButtonElement>;
  textFieldProps?: TextFieldOverrides;
}

function ReadOnlyDateField({
  placeholder,
  value,
  ariaLabel,
  onOpen,
  calendarIconRef,
  textFieldProps,
}: ReadOnlyDateFieldProps) {
  const disabled = textFieldProps?.disabled ?? false;
  const className = textFieldProps?.className
    ? `${textFieldProps.className} ${styles.readOnlyDateInput}`
    : styles.readOnlyDateInput;

  return (
    <TextField
      size="small"
      type="text"
      placeholder={placeholder}
      value={value}
      disabled={disabled}
      aria-label={ariaLabel}
      fullWidth={textFieldProps?.fullWidth}
      variant={textFieldProps?.variant}
      className={className}
      InputProps={{
        readOnly: true,
        endAdornment: (
          <InputAdornment position="end">
            <IconButton
              ref={calendarIconRef}
              size="small"
              onClick={(e) => onOpen(e.currentTarget)}
              disabled={disabled}
              aria-label="Open calendar"
            >
              <CalendarTodayIcon fontSize="small" />
            </IconButton>
          </InputAdornment>
        ),
      }}
      onClick={(e) => {
        if (!disabled) {
          onOpen(e.currentTarget);
        }
      }}
    />
  );
}
